import {
  CalendarDate,
  CalendarDateTime,
  now as internationalizedNow,
  parseAbsoluteToLocal,
  Time,
  toTimeZone,
  ZonedDateTime,
} from "@internationalized/date";

export const DEFAULT_TIMEZONE = "Europe/Paris";

export function formatDate(
  input: string,
  options?: Intl.DateTimeFormatOptions & { locale?: string; timezone?: string },
): string {
  return Intl.DateTimeFormat(options?.locale ?? "fr", {
    timeZone: options?.timezone ?? DEFAULT_TIMEZONE,
    ...options,
  }).format(new Date(input));
}

function toDatePart(value: number, length?: number) {
  return String(value).padStart(length ?? 2, "0");
}

export function toZonedDateTime(date: string, timezone?: string) {
  const parsed = parseAbsoluteToLocal(new Date(date).toISOString());

  return toTimeZone(parsed, timezone ?? DEFAULT_TIMEZONE);
}

export function toDateInputString(
  date: string,
  options?: {
    timezone?: string;
    type?: "date" | "datetime";
  },
) {
  const zonedDateTime = toZonedDateTime(
    date,
    options?.timezone ?? DEFAULT_TIMEZONE,
  );

  if (options?.type === "date") {
    return `${zonedDateTime.year}-${toDatePart(zonedDateTime.month)}-${toDatePart(zonedDateTime.day)}`;
  }

  return `${zonedDateTime.year}-${toDatePart(zonedDateTime.month)}-${toDatePart(zonedDateTime.day)}T${toDatePart(zonedDateTime.hour)}:${toDatePart(zonedDateTime.minute)}:${toDatePart(zonedDateTime.second)}`;
}

export function startOfTomorrow(timezone?: string) {
  return internationalizedNow(timezone ?? DEFAULT_TIMEZONE)
    .add({ days: 1 })
    .set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
}

export function startOfToday(timezone?: string) {
  return internationalizedNow(timezone ?? DEFAULT_TIMEZONE).set({
    hour: 0,
    minute: 0,
    second: 0,
    millisecond: 0,
  });
}

export function now(timezone?: string) {
  const _timezone =
    timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;

  return internationalizedNow(_timezone);
}

export function tomorrow(timezone?: string) {
  const _timezone =
    timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;

  return internationalizedNow(_timezone).add({ days: 1 });
}

export function toNormalizedTimeString(time: Time | DateTimeValue) {
  return `${toDatePart(time.hour)}:${toDatePart(time.minute)}`;
}

export {
  CalendarDate,
  isSameDay,
  isWeekend,
  parseDateTime,
  parseZonedDateTime,
  Time,
  toCalendarDate,
  toCalendarDateTime,
  ZonedDateTime,
} from "@internationalized/date";
export { type ZonedDateTime as ZonedDateTimeType } from "@internationalized/date";

export type DateValue = CalendarDate | CalendarDateTime | ZonedDateTime;

export type DateTimeValue = CalendarDateTime | ZonedDateTime;

/** Same moment, whatever the notation (`Z`, `+00:00`, another offset). */
export function isSameInstant(a?: string | null, b?: string | null) {
  return !!a && !!b && new Date(a).getTime() === new Date(b).getTime();
}

export function getMaxDate(dates: string[]) {
  return new Date(Math.max(...dates.map((date) => new Date(date).getTime())));
}
